import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Text as ChakraText,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import type {
  CollectionBiltoDetailResponse,
  CollectionBiltoHistory,
} from '@diamond/shared/types';
import { DateFilter, Pagination } from '@diamond/shared/ui';
import { formatCurrencyIdr } from '@diamond/shared/utils';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SouthWestIcon from '@mui/icons-material/SouthWest';
import UpdateIcon from '@mui/icons-material/Update';
import { FormProvider, useForm } from 'react-hook-form';

import type { MutationHistoryFilterLabel } from '../../utils/lhc-mutation-history';
import {
  BodyCell,
  HeaderCell,
  LabelText,
  SectionTitle,
  ValueText,
} from '../mutation-drawer-typography';

function formatIsoDateToShort(dateStr: string): string {
  const [year, month, day] = dateStr.split('T')[0]?.split('-') ?? [];

  if (!year || !month || !day) {
    return '-';
  }

  return `${day}/${month}/${year.slice(-2)}`;
}

function getLatestHistoryDate(histories: CollectionBiltoHistory[]): string {
  const latestTransactionAt = histories.reduce((latest, history) => {
    return history.transaction_at > latest ? history.transaction_at : latest;
  }, '');

  return latestTransactionAt ? formatIsoDateToShort(latestTransactionAt) : '-';
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CustomerInfoSection({
  data,
}: {
  data: CollectionBiltoDetailResponse;
}) {
  const { bilto_info, deposit_info } = data;
  const latestHistoryDate = getLatestHistoryDate(data.histories);
  const outletCount = bilto_info.shiptos.length;
  const firstOutletName = bilto_info.shiptos[0]?.name || '-';
  const outletDisplay =
    outletCount > 1
      ? `${firstOutletName}, +${outletCount - 1} Lainnya`
      : firstOutletName;

  return (
    <Flex justify="space-between" gap={4} mb={6}>
      <VStack align="stretch" spacing={4} flex={1}>
        <Box>
          <LabelText mb={1}>Nama Customer</LabelText>
          <ValueText>{bilto_info.customer_name || '-'}</ValueText>
        </Box>
        <Box>
          <LabelText mb={1}>Billing ID</LabelText>
          <ValueText>{bilto_info.sap_no_bilto}</ValueText>
        </Box>
        <Box>
          <LabelText mb={1}>Alamat</LabelText>
          <ChakraText fontSize="14px" color="#373F41" lineHeight="20px">
            {bilto_info.address || '-'}
          </ChakraText>
        </Box>
      </VStack>

      <VStack align="stretch" spacing={4} flex={1}>
        <Box>
          <LabelText mb={1}>Over Payment</LabelText>
          <ValueText color="blue.500" fontWeight={500} fontSize="24px">
            {formatCurrencyIdr(deposit_info.current_balance)}
          </ValueText>
          <Flex align="center" gap={1} mt={1}>
            <UpdateIcon sx={{ fontSize: '14px', color: '#8E8E93' }} />
            <ChakraText fontSize="12px" color="#8E8E93">
              Diperbarui{' '}
              <ChakraText as="span" color="#000000">
                {latestHistoryDate}
              </ChakraText>
            </ChakraText>
          </Flex>
        </Box>
        <Box>
          <LabelText mb={1}>Nama Outlet</LabelText>
          <ValueText>{outletDisplay}</ValueText>
        </Box>
      </VStack>
    </Flex>
  );
}

interface FilterSectionProps {
  activeFilter: MutationHistoryFilterLabel;
  onFilterChange: (filter: MutationHistoryFilterLabel) => void;
  onDateFilterChange: (period: string) => { fromDate: string; toDate: string };
  onDateRangeChange: (fromDate: string, toDate: string) => void;
}

const MUTATION_HISTORY_DATE_FORM_DEFAULTS = {
  'transaction_date-from': '',
  'transaction_date-to': '',
} as const;

function FilterSection({
  activeFilter,
  onFilterChange,
  onDateFilterChange,
  onDateRangeChange,
}: FilterSectionProps) {
  const methods = useForm({
    defaultValues: { ...MUTATION_HISTORY_DATE_FORM_DEFAULTS },
  });

  const filters: Array<{
    label: MutationHistoryFilterLabel;
    icon: JSX.Element | null;
  }> = [
    { label: 'Semua', icon: null },
    {
      label: 'Pending',
      icon: <HourglassEmptyIcon sx={{ fontSize: '16px' }} />,
    },
    { label: 'Mutasi', icon: <SouthWestIcon sx={{ fontSize: '16px' }} /> },
    { label: 'Faktur', icon: <NorthEastIcon sx={{ fontSize: '16px' }} /> },
  ];

  return (
    <Flex justify="space-between" align="flex-end" mb={6}>
      <Box>
        <LabelText mb={2} fontSize="14px" color="#373F41" fontWeight={500}>
          Filter
        </LabelText>
        <Box
          position="relative"
          borderRadius="xl"
          p={1}
          _before={{
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '12px',
            bg: 'azure',
            opacity: 0.12,
            pointerEvents: 'none',
          }}
        >
          <Flex gap="4px" position="relative" zIndex={1}>
            {filters.map((f) => {
              const isActive = activeFilter === f.label;
              return (
                <Button
                  key={f.label}
                  onClick={() => onFilterChange(f.label)}
                  bg={isActive ? 'blue' : 'white'}
                  color={isActive ? 'white' : 'azure'}
                  borderColor={isActive ? 'blue' : 'azure'}
                  borderWidth="1px"
                  _hover={{ bg: isActive ? 'blue' : 'gray.50' }}
                  leftIcon={f.icon || undefined}
                  iconSpacing="10px"
                  w="103.5px"
                  h="36px"
                  px="16px"
                  py="8px"
                  borderRadius="6px"
                  fontSize="12px"
                  fontWeight={500}
                >
                  {f.label}
                </Button>
              );
            })}
          </Flex>
        </Box>
      </Box>

      <Box>
        <LabelText mb={2} fontSize="12px">
          Tanggal Transaksi
        </LabelText>
        <FormProvider {...methods}>
          <Box
            sx={{
              '.chakra-button': {
                bg: 'white',
                borderColor: '#E5E5EA',
                color: '#373F41',
                fontWeight: 400,
                height: '36px',
                px: 3,
                minW: '160px',
                justifyContent: 'space-between',
                _hover: { bg: '#F2F2F7' },
                fontSize: '14px',
              },
              '.MuiSvgIcon-root': {
                fontSize: '16px',
                color: '#8E8E93',
              },
            }}
          >
            <DateFilter
              fromField="transaction_date-from"
              toField="transaction_date-to"
              buttonVariant="outline"
              buttonName="Pilih tanggal"
              shortYearFormat={false}
              onFilterChange={onDateFilterChange}
              onCustomDateChange={onDateRangeChange}
            />
          </Box>
        </FormProvider>
      </Box>
    </Flex>
  );
}

function TransactionTable({ data }: { data: CollectionBiltoDetailResponse }) {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  return (
    <Box>
      <SectionTitle mb={3}>Rincian Mutasi & Faktur</SectionTitle>
      <TableContainer
        border="1px solid"
        borderColor="#E5E5EA"
        borderRadius="8px"
        overflow="hidden"
      >
        <Table variant="simple" size="sm">
          <Thead>
            <Tr bg="#E5E5EA !important">
              <HeaderCell textAlign="left" pl={4}>
                Nomor Mutasi / Faktur
              </HeaderCell>
              <HeaderCell>Jenis Transaksi</HeaderCell>
              <HeaderCell textAlign="right">Nilai Mutasi</HeaderCell>
              <HeaderCell textAlign="right">
                Nilai Faktur/Billing/Nota
              </HeaderCell>
              <HeaderCell w="40px"></HeaderCell>
            </Tr>
          </Thead>
          <Tbody>
            {data.histories.length ? (
              data.histories.map((history: CollectionBiltoHistory) => {
                const isMutasi = history.type === 'bank-mutation';
                const number = isMutasi
                  ? history.bank_mutation_number
                  : history.billing_number;
                const plant = history.billing_plant_name;
                const showMutationAmount =
                  typeof history.bank_mutation_amount === 'number';
                const showBillingAmount =
                  typeof history.billing_amount === 'number';

                return (
                  <Tr
                    key={history.id}
                    _hover={{ bg: '#F9F9F9', cursor: 'pointer' }}
                  >
                    <BodyCell textAlign="left" pl={4}>
                      <VStack align="start" spacing={1}>
                        <ChakraText
                          fontSize="12px"
                          color="#373F41"
                          fontWeight={500}
                        >
                          {formatDate(history.transaction_at)}
                        </ChakraText>
                        <ChakraText fontSize="14px" color="#373F41">
                          {number || '-'} • {plant || '-'}
                        </ChakraText>
                      </VStack>
                    </BodyCell>
                    <BodyCell>
                      {isMutasi ? (
                        <Badge
                          bg="#E6FFED"
                          color="#008000"
                          px={2}
                          py={1}
                          borderRadius="4px"
                          textTransform="none"
                          fontSize="12px"
                          fontWeight={500}
                          display="inline-flex"
                          alignItems="center"
                          gap={1}
                        >
                          <SouthWestIcon sx={{ fontSize: '14px' }} />
                          Mutasi
                        </Badge>
                      ) : (
                        <Badge
                          bg="#E6F0FF"
                          color="#0052CC"
                          px={2}
                          py={1}
                          borderRadius="4px"
                          textTransform="none"
                          fontSize="12px"
                          fontWeight={500}
                          display="inline-flex"
                          alignItems="center"
                          gap={1}
                        >
                          <NorthEastIcon sx={{ fontSize: '14px' }} />
                          Faktur
                        </Badge>
                      )}
                    </BodyCell>
                    <BodyCell textAlign="right">
                      <ChakraText fontSize="14px" color="#373F41">
                        {showMutationAmount
                          ? formatCurrencyIdr(history.bank_mutation_amount)
                          : '-'}
                      </ChakraText>
                    </BodyCell>
                    <BodyCell textAlign="right">
                      <ChakraText fontSize="14px" color="#373F41">
                        {showBillingAmount
                          ? formatCurrencyIdr(history.billing_amount)
                          : '-'}
                      </ChakraText>
                    </BodyCell>
                    <BodyCell pr={4}>
                      <ChevronRightIcon
                        sx={{ color: '#000080', fontSize: '20px' }}
                      />
                    </BodyCell>
                  </Tr>
                );
              })
            ) : (
              <Tr>
                <BodyCell colSpan={5} textAlign="center">
                  Belum ada rincian mutasi & faktur
                </BodyCell>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}

interface PaginationSectionProps {
  data: CollectionBiltoDetailResponse;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

function PaginationSection({
  data,
  onPageChange,
  onLimitChange,
}: PaginationSectionProps) {
  const totalPages = Math.max(data.pagination.total_pages, 1);
  const currentPage = Math.min(Math.max(data.pagination.page, 1), totalPages);
  const handleLimitChange = (value: string) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return;
    }
    onLimitChange(parsed);
  };

  return (
    <Flex justify="center" align="center" mt={6} gap={4}>
      <Pagination
        currentPage={currentPage}
        totalPage={totalPages}
        firstPage={() => onPageChange(1)}
        previousPage={() => onPageChange(Math.max(currentPage - 1, 1))}
        nextPage={() => onPageChange(Math.min(currentPage + 1, totalPages))}
        lastPage={() => onPageChange(totalPages)}
      />
      <Flex align="center" gap={2}>
        <ChakraText fontSize="14px" color="#8E8E93">
          per halaman
        </ChakraText>
        <Select
          size="sm"
          w="70px"
          value={data.pagination.limit}
          color="#373F41"
          borderColor="#E5E5EA"
          onChange={(event) => handleLimitChange(event.target.value)}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </Select>
      </Flex>
    </Flex>
  );
}

interface LHCMutationHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  data: CollectionBiltoDetailResponse | null;
  isLoading: boolean;
  isError: boolean;
  activeFilter: MutationHistoryFilterLabel;
  onFilterChange: (filter: MutationHistoryFilterLabel) => void;
  onDateFilterChange: (period: string) => { fromDate: string; toDate: string };
  onDateRangeChange: (fromDate: string, toDate: string) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

export function LHCMutationHistoryDrawer({
  isOpen,
  onClose,
  data,
  isLoading,
  isError,
  activeFilter,
  onFilterChange,
  onDateFilterChange,
  onDateRangeChange,
  onPageChange,
  onLimitChange,
}: LHCMutationHistoryDrawerProps) {
  const renderBodyContent = () => {
    if (isLoading) {
      return (
        <Flex justify="center" align="center" minH="320px">
          <Spinner size="lg" />
        </Flex>
      );
    }

    if (isError) {
      return (
        <Flex justify="center" align="center" minH="320px">
          <ChakraText color="#8E8E93">
            Gagal memuat riwayat mutasi customer
          </ChakraText>
        </Flex>
      );
    }

    if (!data) {
      return (
        <Flex justify="center" align="center" minH="320px">
          <ChakraText color="#8E8E93">
            Data riwayat mutasi belum tersedia
          </ChakraText>
        </Flex>
      );
    }

    return (
      <>
        <CustomerInfoSection data={data} />
        <FilterSection
          activeFilter={activeFilter}
          onFilterChange={onFilterChange}
          onDateFilterChange={onDateFilterChange}
          onDateRangeChange={onDateRangeChange}
        />
        <TransactionTable data={data} />
        <PaginationSection
          data={data}
          onPageChange={onPageChange}
          onLimitChange={onLimitChange}
        />
      </>
    );
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
      <DrawerOverlay zIndex={1400} />
      <DrawerContent zIndex={1401} w="768px" maxW="768px">
        <DrawerHeader p={4}>
          <Flex align="center" gap={2}>
            <IconButton
              aria-label="Kembali"
              icon={<ArrowBackIosNewIcon fontSize="small" />}
              variant="ghost"
              size="sm"
              onClick={onClose}
            />
            <ChakraText fontWeight={500} fontSize="20px" color="#373F41">
              Riwayat Mutasi dan Faktur Customer
            </ChakraText>
          </Flex>
        </DrawerHeader>
        <DrawerCloseButton zIndex={1402} />
        <DrawerBody p={6}>{renderBodyContent()}</DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
